/**
 * Calendar & Scheduling Settings Tab Component
 * Handles all calendar display, scheduling alerts, and FTE settings
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Clock, AlertCircle, Save } from "lucide-react";

export function CalendarSettingsTab({
  settings,
  onChange,
  onSave,
  saving
}) {
  const handleChange = (field, value) => {
    onChange(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6" data-testid="calendar-settings-tab">
      {/* Calendar Display Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Calendar Display Settings
            </h3>
            <p className="text-sm text-slate-500 mt-1">Configure how calendars and schedules are displayed</p>
          </div>
          <Button 
            onClick={onSave} 
            disabled={saving}
            className="gap-2"
            data-testid="save-scheduling-settings-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>

        <div className="space-y-6">
          {/* Calendar Display Mode */}
          <div className="p-4 bg-slate-50 rounded-lg">
            <Label className="text-sm font-medium text-slate-700 mb-3 block">Calendar Display</Label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="calendar_display"
                  value="month"
                  checked={settings.calendar_display === "month"}
                  onChange={(e) => handleChange("calendar_display", e.target.value)}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm text-slate-700">Entire Month (e.g., Jan 1 - Jan 31)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="calendar_display"
                  value="pay_period"
                  checked={settings.calendar_display === "pay_period"}
                  onChange={(e) => handleChange("calendar_display", e.target.value)}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm text-slate-700">Pay Period View</span>
              </label>
            </div>
          </div>

          {/* Display Options Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SettingToggle
              label="Show entire pay period ending weeks in the month"
              description="Display all weeks that end within the selected month"
              checked={settings.show_pay_period_weeks}
              onChange={(checked) => handleChange("show_pay_period_weeks", checked)}
            />

            <SettingToggle
              label="Default schedules as confirmed"
              description="New schedules are automatically marked as confirmed"
              checked={settings.default_schedules_confirmed}
              onChange={(checked) => handleChange("default_schedules_confirmed", checked)}
            />

            <SettingToggle
              label="Show service code short description with schedules"
              description="Display service code abbreviations on schedule grid"
              checked={settings.show_service_code_description}
              onChange={(checked) => handleChange("show_service_code_description", checked)}
            />

            <SettingToggle
              label="Show payor source with schedules"
              description="Display insurance/payor information on schedules"
              checked={settings.show_payor_source}
              onChange={(checked) => handleChange("show_payor_source", checked)}
            />

            <SettingToggle
              label="Show payroll item with schedules"
              description="Display payroll codes on schedule entries"
              checked={settings.show_payroll_item}
              onChange={(checked) => handleChange("show_payroll_item", checked)}
            />

            <SettingToggle
              label="Enable notes on scheduling grid and time sheets"
              description="Allow adding notes directly on schedule entries"
              checked={settings.enable_notes_on_grid}
              onChange={(checked) => handleChange("enable_notes_on_grid", checked)}
            />

            <SettingToggle
              label="Show caregiver unavailable time"
              description="Display blocked time slots for caregivers"
              checked={settings.show_caregiver_unavailable}
              onChange={(checked) => handleChange("show_caregiver_unavailable", checked)}
            />

            <SettingToggle
              label="Show Task Completed Status"
              description="Display checkmarks for completed tasks"
              checked={settings.show_task_completed_status}
              onChange={(checked) => handleChange("show_task_completed_status", checked)}
            />

            <SettingToggle
              label="Remove Client Phone Number"
              description="Hide phone numbers from schedule displays"
              checked={settings.remove_client_phone}
              onChange={(checked) => handleChange("remove_client_phone", checked)}
            />

            <SettingToggle
              label="Show hours on scheduling Grid and Calendar"
              description="Display total hours for each schedule entry"
              checked={settings.show_hours_on_grid}
              onChange={(checked) => handleChange("show_hours_on_grid", checked)}
            />

            <SettingToggle
              label="Enable quick Confirm on schedule Grid"
              description="One-click confirm without conflict alerts"
              checked={settings.enable_quick_confirm}
              onChange={(checked) => handleChange("enable_quick_confirm", checked)}
            />
          </div>
        </div>
      </div>

      {/* Alerts & Notifications */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-6">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          Scheduling Alerts & Notifications
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <Label className="text-sm font-medium text-slate-700">Alert when caregiver Attributes do not match client needs</Label>
              <p className="text-xs text-slate-500 mt-1">Warn when assigning caregivers without required skills</p>
            </div>
            <Switch
              checked={settings.alert_caregiver_attributes_mismatch}
              onCheckedChange={(checked) => handleChange("alert_caregiver_attributes_mismatch", checked)}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
            <div>
              <Label className="text-sm font-medium text-slate-700">Enable Conflict Alerts on EVV Schedules</Label>
              <p className="text-xs text-slate-500 mt-1">Show warnings for overlapping EVV entries</p>
            </div>
            <Switch
              checked={settings.enable_evv_conflict_alerts}
              onCheckedChange={(checked) => handleChange("enable_evv_conflict_alerts", checked)}
            />
          </div>
        </div>
      </div>

      {/* FTE Alert Settings */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-6">
          <Clock className="w-5 h-5 text-blue-500" />
          FTE (Full-Time Equivalent) Alerts
        </h3>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <Label className="text-sm font-medium text-slate-700">Enable FTE Alert (weekly)</Label>
              <p className="text-xs text-slate-500 mt-1">Alert when caregiver approaches overtime threshold</p>
            </div>
            <Switch
              checked={settings.enable_fte_alert}
              onCheckedChange={(checked) => handleChange("enable_fte_alert", checked)}
            />
          </div>

          {settings.enable_fte_alert && (
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-sm font-medium text-slate-700 mb-2 block">FTE Alert after exceeding # hours</Label>
              <div className="flex items-center gap-4">
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={settings.fte_alert_hours}
                  onChange={(e) => handleChange("fte_alert_hours", parseInt(e.target.value) || 40)}
                  className="w-24"
                />
                <span className="text-sm text-slate-600">hours per week</span>
              </div>
              <p className="text-xs text-slate-500 mt-2">Standard full-time is 40 hours. Alert triggers when scheduled hours exceed this threshold.</p>
            </div>
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">About Calendar & Scheduling Settings</h4>
            <ul className="text-sm text-blue-800 mt-1 space-y-1">
              <li>• These settings affect how schedules appear on the calendar, grid views, and timesheets.</li>
              <li>• FTE alerts help prevent overtime by warning when caregivers approach their hour limits.</li>
              <li>• Conflict alerts help ensure proper coverage without double-booking.</li>
              <li>• Changes take effect immediately after saving.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component for toggle settings
function SettingToggle({ label, description, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
      <div>
        <Label className="text-sm font-medium text-slate-700">{label}</Label>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}
